"use client";

// Leadership bio modal — Wayflyer redesign (Figma 6436-28946).
// Opens when a leader's portrait is clicked. A grey-50 card, centered with a
// fixed height: the portrait stays pinned at the top while the bio text scrolls
// (with a bottom fade) when it overflows. A custom scroll indicator (Figma
// 6436-28944) appears on the right whenever the text overflows. Built on the
// Radix dialog primitive for focus-trap, ESC-to-close, and scroll-locking.

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Xmark } from "iconoir-react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link } from "@/components/ui/link";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import { cn } from "@/lib/utils";

import { LeadershipModalPortrait } from "./leadership-portrait";
import type { Leader } from "./leadership-section";
import { LinkedInIcon } from "./linkedin-icon";

interface LeadershipModalProps {
  leader: Leader | null;
  closeLabel: string;
  onClose: () => void;
}

// Brand-colour glow behind the card (Figma 6436-28945). The Figma source is a
// 966×1208 ellipse with the brand gradient rotated 90° — but its coral and blue
// land on opposite diagonal corners, so a horizontal band through it only shows
// the purple middle. To reproduce the rendered look (blue left → coral right),
// the gradient runs horizontally with blue holding the left ~third, then the
// brand stops compressing through magenta to coral on the right.
const GLOW_GRADIENT =
  "linear-gradient(90deg, #001EFF 0%, #001EFF 33%, #6B20FC 50%, #C739CF 66%, #E040B4 80%, #F14592 92%, #FE634D 100%)";

// Figma 6436:28991 — modal text styles (title/md + body/lg/sm with modal line-height overrides).
const MODAL_FONT =
  "[font-feature-settings:'ss03','ss01','zero','ss02']" as const;
const MODAL_NAME = "text-ds-heading-h3 !text-grey-950 font-normal" as const;
const MODAL_BODY_LG = "text-ds-body-lg leading-[1.3] text-grey-600" as const;
const MODAL_LEAD = "text-ds-body-lg leading-[1.3] text-grey-950" as const;
const MODAL_BODY_SM =
  "text-ds-body-sm leading-[1.5] text-grey-700 max-w-[538px]" as const;

// Smallest the thumb is allowed to get on very long bios, as a % of the track.
const MIN_THUMB_PCT = 12;

function paragraphs(text: string): string[] {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

/**
 * Splits a leader's copy into the 18px lead paragraph and the 14px body. Aidan
 * carries a dedicated `intro`; John/Deirdre have an empty `intro`, so their first
 * `description` paragraph becomes the lead. The remaining paragraphs are joined
 * into one continuous body block to match the Figma (6436-28947).
 */
function splitBio(leader: Leader): { lead: string; body: string } {
  if (leader.intro && leader.intro.trim()) {
    return {
      lead: leader.intro.trim(),
      body: paragraphs(leader.description).join(" "),
    };
  }
  const [first, ...rest] = paragraphs(leader.description);
  return { lead: first ?? "", body: rest.join(" ") };
}

interface ThumbState {
  visible: boolean;
  heightPct: number;
  topPct: number;
  atBottom: boolean;
}

export function LeadershipModal({
  leader,
  closeLabel,
  onClose,
}: LeadershipModalProps) {
  const open = leader !== null;
  const { lead, body } = leader ? splitBio(leader) : { lead: "", body: "" };
  const followLabel = leader?.followLabel ?? null;

  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<ThumbState>({
    visible: false,
    heightPct: 0,
    topPct: 0,
    atBottom: true,
  });

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb({ visible: false, heightPct: 0, topPct: 0, atBottom: true });
      return;
    }
    const heightPct = Math.max(
      MIN_THUMB_PCT,
      (clientHeight / scrollHeight) * 100
    );
    // Keep the thumb inside the track once its minimum height kicks in.
    const maxTop = 100 - heightPct;
    const topPct = Math.min(maxTop, (scrollTop / scrollHeight) * 100);
    const atBottom = scrollTop + clientHeight >= scrollHeight - 2;
    setThumb({ visible: true, heightPct, topPct, atBottom });
  }, []);

  // Re-measure when the modal opens (content mounts), as the layout settles
  // (ResizeObserver catches the flex/90dvh constraint applying), and on resize.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    updateThumb();
    const observer = new ResizeObserver(() => updateThumb());
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    window.addEventListener("resize", updateThumb);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateThumb);
    };
  }, [open, leader, updateThumb]);

  // Drag the thumb to scroll.
  const onThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const startY = event.clientY;
    const startScroll = el.scrollTop;
    const trackHeight = track.clientHeight;
    const { scrollHeight } = el;

    const onMove = (moveEvent: PointerEvent) => {
      const deltaY = moveEvent.clientY - startY;
      el.scrollTop = startScroll + (deltaY / trackHeight) * scrollHeight;
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className="z-50 grid place-items-center overflow-y-auto bg-white/50 p-4 backdrop-blur-[15px]">
          {/* Blurred brand-gradient ellipse, anchored low so it sits behind the
              lower card and rises on the sides (Figma 6436-28945). */}
          <div
            aria-hidden
            className="pointer-events-none fixed bottom-0 left-1/2 z-0 h-[760px] w-[1280px] -translate-x-1/2 translate-y-[40%] rounded-[50%] opacity-50 blur-[120px]"
            style={{ backgroundImage: GLOW_GRADIENT }}
          />
        </DialogOverlay>

        <DialogPrimitive.Popup
          aria-describedby={undefined}
          className={cn(
            // Fade-only entrance — bypass ui/dialog slide-from-center defaults.
            "data-open:animate-in data-closed:animate-out",
            "data-closed:fade-out-0 data-open:fade-in-0",
            "data-closed:zoom-out-[0.98] data-open:zoom-in-[0.98]",
            "data-closed:slide-out-to-left-0 data-closed:slide-out-to-top-0",
            "data-open:slide-in-from-left-0 data-open:slide-in-from-top-0",
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "bg-grey-50 rounded-ds-xl z-[60] flex h-[850px] max-h-[90dvh] w-[635px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden border-0 p-3 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1)]",
            "origin-center duration-200 ease-out motion-reduce:animate-none"
          )}
        >
          {leader && (
            <>
              {/* Portrait — pinned at the top. */}
              <div className="bg-grey-200 rounded-t-ds-m relative h-[332px] shrink-0 overflow-hidden">
                {leader.portraits ? (
                  <LeadershipModalPortrait
                    src={leader.portraits.modalSrc}
                    alt={leader.image?.alt ?? leader.name}
                    frame={leader.portraits.modal}
                  />
                ) : leader.image?.src ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={leader.image.src}
                    alt={leader.image.alt}
                    className="size-full object-cover object-top"
                  />
                ) : null}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={closeLabel}
                  className="border-grey-100 text-grey-950 hover:bg-grey-50 absolute top-5 right-5 grid size-7 place-items-center rounded-full border bg-white transition-colors"
                >
                  <Xmark className="size-3" aria-hidden />
                </button>
              </div>

              {/* Bio — scrolls when it overflows the fixed-height card. */}
              <div className="rounded-b-ds-m relative flex min-h-0 flex-1 flex-col bg-white">
                <div
                  ref={scrollRef}
                  onScroll={updateThumb}
                  className="min-h-0 flex-1 overflow-y-auto px-[27px] pt-8 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                >
                  {/* 557px text column (name/title/lead); body caps at 538px — Figma 6436-28947. */}
                  <div
                    className={cn(
                      "flex w-full max-w-[557px] flex-col gap-5",
                      MODAL_FONT
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      <DialogTitle className={cn(MODAL_NAME, MODAL_FONT)}>
                        {leader.name}
                      </DialogTitle>
                      <p className={cn(MODAL_BODY_LG, MODAL_FONT)}>
                        {leader.position}
                      </p>
                    </div>
                    {lead && (
                      <ScrollRevealText
                        key={leader.id}
                        as="p"
                        mode="whole"
                        variant="light"
                        className={cn(MODAL_LEAD, MODAL_FONT)}
                      >
                        {lead}
                      </ScrollRevealText>
                    )}
                    {body && (
                      <p className={cn(MODAL_BODY_SM, MODAL_FONT)}>{body}</p>
                    )}
                  </div>
                </div>

                {/* Bottom fade — signals more content below the fold (Figma
                    6436-28944). Shown only while the bio overflows and the user
                    hasn't reached the end, so the last line stays readable. */}
                <div
                  aria-hidden
                  className={cn(
                    "rounded-b-ds-m pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-b from-transparent to-white transition-opacity duration-200",
                    thumb.visible && !thumb.atBottom
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />

                {/* Custom scroll indicator (Figma 6436-28944) — only when the
                    bio overflows. 4px rounded track + draggable grey thumb. */}
                {thumb.visible && (
                  <div
                    ref={trackRef}
                    aria-hidden
                    className="bg-grey-50 absolute top-2 right-2 bottom-2 w-1 rounded-full"
                  >
                    <div
                      onPointerDown={onThumbPointerDown}
                      className="bg-grey-200 hover:bg-grey-300 absolute left-0 w-full cursor-grab touch-none rounded-full transition-colors active:cursor-grabbing"
                      style={{
                        height: `${thumb.heightPct}%`,
                        top: `${thumb.topPct}%`,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* LinkedIn footer — on the grey-50 frame below the bio. The
                  bottom padding is larger than the 12px side frame (Figma
                  6436-28863) so the link sits well clear of the card edge. */}
              {leader.linkedinUrl && (
                <div className="shrink-0 px-8 pt-5 pb-3">
                  <Link
                    href={leader.linkedinUrl}
                    external
                    className="text-ds-body-sm inline-flex items-center gap-[5px] leading-[1.5] tracking-[-0.28px] no-underline"
                  >
                    <span className={cn("text-grey-700", MODAL_FONT)}>
                      {followLabel ?? leader.name}
                    </span>
                    <span className="text-grey-600 hover:text-pulse-700 transition-colors duration-200">
                      <LinkedInIcon className="size-5" />
                    </span>
                  </Link>
                </div>
              )}
            </>
          )}
        </DialogPrimitive.Popup>
      </DialogPortal>
    </Dialog>
  );
}
